import { Component, OnDestroy, OnInit, inject, input } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { ModalController } from '@ionic/angular/standalone';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { AuthService } from '../../../auth/auth.service';
import { LinkedPayeesListComponent } from '../../linked-payees-list/linked-payees-list.component';
import { PayeeService } from 'src/app/services/payee.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { DbService } from 'src/app/services/db.service';
import { ContractService } from 'src/app/services/contract.service';
import { NgClass, AsyncPipe } from '@angular/common';
import { addIcons } from "ionicons";
import { cashOutline, personCircleOutline, swapHorizontalOutline, lockClosedOutline, logOutOutline } from "ionicons/icons";
import { IonRouterLinkWithHref, IonToolbar, IonButtons, IonBackButton, IonPopover, IonIcon, IonTitle } from "@ionic/angular/standalone";
import { PayeeInfoComponent } from '../../payee-info/payee-info.component';
import { NewPasswordComponent } from '../../../pages/login/new-password/new-password.component';

@Component({
  selector: 'app-base-header',
  templateUrl: './base-header.component.html',
  styleUrls: ['./base-header.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    RouterLink,
    RouterLinkActive,
    AsyncPipe,
    IonRouterLinkWithHref,
    IonToolbar,
    IonButtons,
    IonBackButton,
    IonPopover,
    IonIcon,
    IonTitle
  ],
})
export class BaseHeaderComponent implements OnInit, OnDestroy {
  payeeService = inject(PayeeService);
  auth = inject(AuthService);
  private modalController = inject(ModalController);
  private router = inject(Router);
  navigationService = inject(NavigationService);
  private db = inject(DbService);
  contractService = inject(ContractService);


  readonly backButton = input<boolean>(undefined);
  readonly showForLg = input<boolean>(undefined);
  readonly showBackground = input<boolean>(undefined);
  readonly hideForLg = input(true);
  readonly titleInput = input<string | null>(null, { alias: 'title' });
  title: string;

  joinSubscription: Subscription;
  routerSubscription: Subscription;
  payeeName$: Observable<string>;
  showManagerButton$: Observable<boolean>;
  loggedIn$: Observable<boolean>;
  isAdminContext = false;
  isManagementActive = false;
  userPopoverOpen = false;
  clickEvent: Event;
  userPopoverReference = 'trigger';
  private lastBpaRoute = '/pages/earnings';
  private lastAdminRoute = '/pages/admin';
  private readonly managementRoutes = ['/pages/admin/admin-reports', '/pages/admin/ferramenta', '/pages/admin/config'];

  constructor() {
    addIcons({cashOutline,personCircleOutline,swapHorizontalOutline,lockClosedOutline,logOutOutline});
  }

  ngOnInit() {
    this.title = this.titleInput() || '';

    this.payeeName$ = this.payeeService.signedInPayee$.pipe(
      map(payee => payee?.companyName)
    );

    this.showManagerButton$ = this.payeeService.signedInPayee$.pipe(
      map(payee =>
        (payee?.internalProperties?.linkedPayees?.length || 0) > 0 ||
        !!payee?.internalProperties?.parentPayee
      )
    );

    this.loggedIn$ = this.auth.user$.pipe(
      map(user => !!user)
    );

    this.setAdminContext(this.router.url);
    this.routerSubscription = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event) => {
      this.setAdminContext(event.urlAfterRedirects);
    });
  }

  async showSelectPayeeModal() {
    const modal = await this.modalController.create({
      component: LinkedPayeesListComponent,
      canDismiss: true,
      cssClass: 'payees-list'
    });
    await modal.present();

    const { data } = await modal.onDidDismiss();
    if (data) {
      this.payeeService.selectPayeeId(data);
      this.router.navigate(['/pages/earnings']);
    }
  }

  async presentUserPopover(event: Event) {
    this.userPopoverReference = 'event';
    this.clickEvent = event;
    this.userPopoverOpen = true;
  }

  navigateUser(destination: 'payment-data' | 'account') {
    this.userPopoverOpen = false;
    if (destination === 'payment-data') {
      this.openPaymentDataPopover();
    } else if (destination === 'account') {
      this.openNewPasswordModal();
    }
  }

  async openPaymentDataPopover() {
    const signedInPayee = await firstValueFrom(this.payeeService.signedInPayee$);
    let payee = signedInPayee;
    
    if (signedInPayee?.internalProperties?.parentPayee) {
      payee = await firstValueFrom(this.db.getPayee(signedInPayee.internalProperties.parentPayee));
    }
    
    const modal = await this.modalController.create({
      component: PayeeInfoComponent,
      canDismiss: true,
      componentProps: {
        payee,
        paymentOnly: true,
        showClose: true,
      },
      cssClass: 'payee-info-simple'
    });
    await modal.present();
  }

  async openNewPasswordModal() {
    const modal = await this.modalController.create({
      component: NewPasswordComponent,
      canDismiss: true,
      cssClass: 'payee-info-simple'
    });
    await modal.present();
  }

  async signOut() {
    await this.auth.signOut();
  }

  switchContext(target: 'bpa' | 'admin'): void {
    const route = target === 'admin' ? this.lastAdminRoute : this.lastBpaRoute;
    this.router.navigateByUrl(route);
  }

  private setAdminContext(url: string): void {
    const isAdmin = url.startsWith('/pages/admin');

    if (isAdmin) {
      this.lastAdminRoute = url;
    } else if (url.startsWith('/pages/')) {
      this.lastBpaRoute = url;
    }

    this.isAdminContext = isAdmin;
    this.isManagementActive = this.managementRoutes.some(route => url.startsWith(route));
  }

  ngOnDestroy(): void {
    this.joinSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }

}
